import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Lottie from 'lottie-react';
import { X, Clock, Zap, Award, TrendingUp } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { getStreakStatus, formatPoints } from '@/lib/points';
import { cn } from '@/lib/utils';
import fireAnimation from '@/animations/fire.json';

import './StreakDisplay.css';

const DAY_MS = 24 * 60 * 60 * 1000;
const BUMP_MS = 720;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const pad = (n: number) => String(n).padStart(2, '0');

const Flame: React.FC<{ size: number; reduced: boolean }> = ({
  size,
  reduced,
}) => (
  <Lottie
    animationData={fireAnimation}
    loop={!reduced}
    autoplay={!reduced}
    style={{ width: size, height: size }}
  />
);

/**
 * Momentum-streak indicator: a flame pill pinned bottom-right (beside the nav),
 * always visible while a streak is live, that expands upward into a card with a
 * depleting 24h countdown ring, the multiplier, and streak chips. The flame
 * bumps + wobbles whenever the multiplier increases. Self-hides at ≤ 1.0×.
 * Mounted globally in App so it shows on every view (incl. review pages).
 */
const StreakDisplay: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [bumping, setBumping] = useState(false);
  const reduced = useRef(prefersReducedMotion()).current;
  const cardRef = useRef<HTMLDivElement>(null);
  const seeded = useRef(false);
  const prevMult = useRef(1);

  const { data } = useQuery({
    queryKey: ['points', 'streak'],
    queryFn: () => getStreakStatus(token as string),
    enabled: !!token,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  // Refresh instantly when a record is submitted elsewhere in the app.
  useEffect(() => {
    const handler = () =>
      queryClient.invalidateQueries({ queryKey: ['points', 'streak'] });
    window.addEventListener('recordSubmitted', handler);
    return () => window.removeEventListener('recordSubmitted', handler);
  }, [queryClient]);

  const multiplier = data?.streak_multiplier ?? 1;
  const expiresAt = useMemo(
    () =>
      data?.streak_expires_at ? new Date(data.streak_expires_at).getTime() : 0,
    [data?.streak_expires_at],
  );
  const remainingMs = expiresAt ? expiresAt - now : 0;
  const active = multiplier > 1.0 && remainingMs > 0;

  // Tick once a second while a streak is live (drives ring + countdown).
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  // Flame bump+wobble when the multiplier *increases* (a new upload). The first
  // data load just seeds the baseline so we don't bump on page open.
  useEffect(() => {
    if (!data) return;
    if (seeded.current && multiplier > prevMult.current && !reduced) {
      setBumping(true);
      const bumpTimeout = window.setTimeout(() => setBumping(false), BUMP_MS);
      prevMult.current = multiplier;
      return () => window.clearTimeout(bumpTimeout);
    }
    prevMult.current = multiplier;
    seeded.current = true;
  }, [multiplier, data, reduced]);

  // Click outside the expanded card closes it.
  useEffect(() => {
    if (!expanded) return;
    const onDown = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [expanded]);

  if (!token || !active) return null;

  const fraction = Math.min(1, Math.max(0, remainingMs / DAY_MS));
  const totalSec = Math.max(0, Math.floor(remainingMs / 1000));
  const hh = Math.floor(totalSec / 3600);
  const mm = Math.floor((totalSec % 3600) / 60);
  const ss = totalSec % 60;
  const timeStr = `${pad(hh)}:${pad(mm)}:${pad(ss)}`;

  // Ring warms amber -> red as the deadline nears (goal-gradient): both gradient
  // stops shift toward red (hue 0) as the remaining fraction drops.
  const ringFrom = `hsl(${Math.round(45 * fraction)}, 95%, 58%)`;
  const ringTo = `hsl(${Math.round(22 * fraction)}, 90%, 48%)`;
  const nextMultiplier = Math.round((multiplier + 0.1) * 10) / 10;
  const pointsThisStreak = data?.points_this_streak ?? 0;

  const R = 44;
  const C = 2 * Math.PI * R;
  const dashOffset = C * (1 - fraction);

  if (!expanded) {
    return (
      <button
        type="button"
        className={cn('corpus-streak-bubble', bumping && 'is-bump')}
        onClick={() => setExpanded(true)}
        aria-label={`Streak multiplier ${multiplier.toFixed(1)}×, tap for details`}
      >
        <span
          className={cn('corpus-streak-bubble__flame', bumping && 'is-bump')}
        >
          <Flame size={28} reduced={reduced} />
        </span>
        <span className="corpus-streak-bubble__mult">
          {multiplier.toFixed(1)}×
        </span>
      </button>
    );
  }

  return (
    <div
      ref={cardRef}
      className="corpus-streak-card"
      role="dialog"
      aria-label={t('categories.streakStatus')}
    >
      <button
        type="button"
        className="corpus-streak-card__close"
        onClick={() => setExpanded(false)}
        aria-label={t('common.collapse.streak.card')}
      >
        <X size={15} />
      </button>

      <div className="corpus-streak-card__top">
        <div className="corpus-streak-card__ringwrap">
          <svg viewBox="0 0 100 100" className="corpus-streak-card__ring">
            <defs>
              <linearGradient
                id="corpus-streak-grad"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor={ringFrom} />
                <stop offset="100%" stopColor={ringTo} />
              </linearGradient>
            </defs>
            <circle
              cx="50"
              cy="50"
              r={R}
              className="corpus-streak-card__track"
              fill="none"
            />
            <circle
              cx="50"
              cy="50"
              r={R}
              fill="none"
              stroke="url(#corpus-streak-grad)"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={dashOffset}
              transform="rotate(-90 50 50)"
              style={{ transition: 'stroke-dashoffset 1s linear' }}
            />
          </svg>
          <div className="corpus-streak-card__flame">
            <span
              className={cn(
                'corpus-streak-card__flame-anim',
                bumping && 'is-bump',
              )}
            >
              <Flame size={46} reduced={reduced} />
            </span>
            <span className="corpus-streak-card__mult">
              {multiplier.toFixed(1)}×
            </span>
          </div>
        </div>

        <div className="corpus-streak-card__info">
          <span className="corpus-streak-card__status">
            <span className="corpus-streak-card__dot" aria-hidden="true" />
            {t('common.streak.active')}
          </span>
          <span className="corpus-streak-card__time">
            <Clock size={16} className="corpus-streak-card__clock" />
            {timeStr}
          </span>
          <span className="corpus-streak-card__sub">
            {t('time.leftInThis24hourWindow')}
          </span>
        </div>
      </div>

      <div className="corpus-streak-card__chips">
        <div className="corpus-streak-card__chip">
          <span className="corpus-streak-card__chip-label">
            <Award size={13} />
            {t('stats.pointsThisStreak')}
          </span>
          <span className="corpus-streak-card__chip-value corpus-streak-card__chip-value--accent">
            {formatPoints(pointsThisStreak)} pts
          </span>
        </div>
        <div className="corpus-streak-card__chip">
          <span className="corpus-streak-card__chip-label">
            <TrendingUp size={13} />
            {t('common.nextUpload')}
          </span>
          <span className="corpus-streak-card__chip-value">
            {multiplier.toFixed(1)}×{' '}
            <span className="corpus-streak-card__chip-arrow">→</span>{' '}
            <span className="corpus-streak-card__chip-next">
              {nextMultiplier.toFixed(1)}×
            </span>
          </span>
        </div>
      </div>

      <p className="corpus-streak-card__caption">
        <Zap size={12} className="corpus-streak-card__caption-icon" />
        {t(
          'common.onlyUploadsRaiseYourMultiplierWhenThe24hTimerEndsItResetsTo1',
        )}
      </p>
    </div>
  );
};

export default StreakDisplay;
